import * as React from 'react';
import {
    Edit,
    TextInput,
    BooleanInput,
    required,
    SaveButton,
    useRecordContext,
    useNotify,
    useRedirect,
    Toolbar,
    SelectArrayInput,
    ReferenceArrayInput,
    SelectInput,
    ReferenceInput,
    TabbedForm,
    FormTab
} from 'react-admin';
import { orgsFields } from '../orgs.model';
import { validatePhone } from '../../utils/validation';
import ProviderRoleOrderedList from '../../components/ProviderRoleOrderedList';
import { US_STATES_RESOURCE_NAME } from '../../usStates/usStates.dataProvider';
import { ENROLLMENT_METHOD_OPTIONS } from '../../utils/constants';
import { validatePayerId, validateHealthieInsuranceId } from './utils/formValidation';
import { INSURANCE_PLANS_RESOURCE_NAME as INSURANCE_PLANS_RESOURCE } from '../../insurancePlans/insurance.plans.dataProvider';
import { SectionedForm, SectionedFormLayout } from "../../components/layouts";
import ProductFeatureToggles from './productFeatureToggles.component';
import { useGrowthBook } from '@growthbook/growthbook-react';

const EditToolbar = () => (
    <Toolbar>
        <SaveButton label="SAVE" />
    </Toolbar>
);

const OrgTitle = () => {
    const record = useRecordContext();
    return record ? `Organization ${ record[ orgsFields.orgName ] }` : '';
};

const OrgEdit = () => {
    const notify = useNotify();
    const redirect = useRedirect();
    const growthbook = useGrowthBook();

    const onSuccess = () => {
        notify('Organization updated successfully');
        redirect('list', 'organizations');
    };

    const onError = (error) => {
        notify(error?.reason || error?.message || 'Error updating the organization', { type: 'error' });
    };

    const sections = [
        {
            title: "Basic Information",
            fields: [
                <TextInput
                    key="orgName"
                    source={orgsFields.orgName}
                    label='Name'
                    validate={required()}
                />,
                <SelectInput
                    key="enrollmentMethod"
                    source={orgsFields.enrollmentMethod}
                    choices={ENROLLMENT_METHOD_OPTIONS}
                    validate={required()}
                />,
                <TextInput
                    key="payerId"
                    source={orgsFields.payerId}
                    label="Payer ID"
                    validate={validatePayerId}
                />,
                <ReferenceInput
                    key="healthieInsurance"
                    source={orgsFields.healthieInsuranceId}
                    reference={INSURANCE_PLANS_RESOURCE}
                >
                    <SelectInput
                        optionText="name_and_id"
                        validate={validateHealthieInsuranceId}
                    />
                </ReferenceInput>,
                <TextInput
                    key="nursePhone"
                    source={orgsFields.orgNursePhone}
                    label='Nurse Phone'
                    validate={validatePhone()}
                />,
                <TextInput
                    key="clientGroupId"
                    source={orgsFields.clientGroupId}
                    label="Healthie Client Group ID"
                />
            ]
        },
        {
            title: "Feature Settings",
            fields: [
                <BooleanInput
                    key="isActive"
                    source={orgsFields.isActive}
                    label="Is Active"
                />,
                <BooleanInput
                    key="contentLibrary"
                    source={orgsFields.contentLibraryEnabled} 
                    label="Content Library Enabled"
                />,
                <BooleanInput
                    key="medicaidId"
                    source={orgsFields.medicaidIdRegistrationQuestionEnabled}
                    label="Medicaid ID Registration Question Enabled"
                />,
                <BooleanInput
                    key="chatEnabled"
                    source={orgsFields.chatEnabled}
                    label="Chat Enabled"
                />,
                <BooleanInput
                    key="enrollmentCode"
                    source={orgsFields.enrollmentCodeRequired}
                    label="Enrollment Code Required"
                />,
                <BooleanInput
                    key="wicId"
                    source={orgsFields.requiresWicId}
                    label="WIC ID Required"
                />
            ]
        },
        {
            title: "Configuration",
            fields: [
                <ReferenceArrayInput
                    key="usStates"
                    source={orgsFields.usStates}
                    perPage={10000}
                    sort={{ field: "name", order: 'ASC' }}
                    reference={US_STATES_RESOURCE_NAME}
                    label="US States"
                    allowEmpty
                >
                    <SelectArrayInput optionText="name" />
                </ReferenceArrayInput>,
                <ProviderRoleOrderedList
                    key="providerRoles"
                    rolesFieldName={orgsFields.roleIds}
                />
            ]
        }
    ];

    const showProductFeatures = growthbook && growthbook.isOn('enableAdminOrganizationFeatureToggles');

    // If feature flag is disabled, use original 3-column layout
    if (!showProductFeatures) {
        return (
            <Edit
                title={<OrgTitle />}
                mutationMode="pessimistic"
                mutationOptions={{
                    onSuccess,
                    onError
                }}
            >
                <SectionedForm sections={sections} toolbar={<EditToolbar />} />
            </Edit>
        );
    }

    // If feature flag is enabled, add Product Features tab
    return (
        <Edit
            title={<OrgTitle />}
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess,
                onError
            }}
        >
            <TabbedForm toolbar={<EditToolbar />} syncWithLocation={false}>
                <FormTab label="Details" path="details">
                    <SectionedFormLayout sections={sections} />
                </FormTab>
                <FormTab label="Product Features" path="product-features">
                    <ProductFeatureToggles />
                </FormTab>
            </TabbedForm>
        </Edit>
    );
};

export default OrgEdit;
